import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { Button } from '../../components/Button.js';
import { Input } from '../../components/Input.js';
import { useAuth } from './useAuth.js';
import { ApiError } from '../../lib/api-client.js';

export function LoginView() {
  const { user, loading: authLoading, signInWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/map';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Googleリダイレクト後にこのページに戻った場合、認証済みなら自動でリダイレクト
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmail(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ログインに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      // signInWithRedirectの場合、ここでページ遷移が発生するため以降は実行されない
      // 戻り後の遷移はuseEffect内のauthLoading/user監視で処理する
    } catch {
      setError('Googleログインに失敗しました');
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-white">
      <img src="/favicon.svg" alt="RideHub" className="w-24 h-24 mb-2" />
      <p className="text-gray-500 mb-8">サイクリスト向けスポット共有アプリ</p>

      <form onSubmit={handleEmailLogin} className="w-full max-w-sm flex flex-col gap-4">
        <Input
          label="メールアドレス"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
        <Input
          label="パスワード"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />

        {error && <p className="text-sm text-red-500">{error}</p>}

        <Button type="submit" loading={loading} size="lg" className="w-full">
          ログイン
        </Button>

        <div className="relative flex items-center my-2">
          <div className="flex-1 border-t border-gray-200" />
          <span className="mx-4 text-sm text-gray-400">または</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={handleGoogleLogin}
        >
          <span>G</span> Googleでログイン
        </Button>

        <div className="flex flex-col items-center gap-2 mt-4 text-sm text-gray-500">
          <Link to="/register" className="text-primary hover:underline">
            アカウントを作成
          </Link>
          <button type="button" className="text-primary hover:underline">
            パスワードを忘れた方
          </button>
        </div>
      </form>
    </div>
  );
}
